import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

// Bible book groups (same as study-analytics-dashboard)
const BIBLE_BOOK_GROUPS = [
  { id: 'pentateuch', label: 'Pentateuch', testament: 'OT', books: ['Genesis', 'Exodus', 'Leviticus', 'Numbers', 'Deuteronomy'] },
  { id: 'historical', label: 'Historical Books', testament: 'OT', books: ['Joshua', 'Judges', 'Ruth', '1 Samuel', '2 Samuel', '1 Kings', '2 Kings', '1 Chronicles', '2 Chronicles', 'Ezra', 'Nehemiah', 'Esther'] },
  { id: 'poetry', label: 'Poetry & Wisdom', testament: 'OT', books: ['Job', 'Psalms', 'Proverbs', 'Ecclesiastes', 'Song of Solomon'] },
  { id: 'major-prophets', label: 'Major Prophets', testament: 'OT', books: ['Isaiah', 'Jeremiah', 'Lamentations', 'Ezekiel', 'Daniel'] },
  { id: 'minor-prophets', label: 'Minor Prophets', testament: 'OT', books: ['Hosea', 'Joel', 'Amos', 'Obadiah', 'Jonah', 'Micah', 'Nahum', 'Habakkuk', 'Zephaniah', 'Haggai', 'Zechariah', 'Malachi'] },
  { id: 'gospels', label: 'Gospels', testament: 'NT', books: ['Matthew', 'Mark', 'Luke', 'John'] },
  { id: 'acts', label: 'Acts', testament: 'NT', books: ['Acts'] },
  { id: 'pauline', label: 'Pauline Epistles', testament: 'NT', books: ['Romans', '1 Corinthians', '2 Corinthians', 'Galatians', 'Ephesians', 'Philippians', 'Colossians', '1 Thessalonians', '2 Thessalonians', '1 Timothy', '2 Timothy', 'Titus', 'Philemon'] },
  { id: 'general-epistles', label: 'General Epistles', testament: 'NT', books: ['Hebrews', 'James', '1 Peter', '2 Peter', '1 John', '2 John', '3 John', 'Jude'] },
  { id: 'revelation', label: 'Revelation', testament: 'NT', books: ['Revelation'] },
];

const PHASE_LABELS = {
  0: 'Phase 0 — Not Started',
  1: 'Phase 1 — Daily',
  2: 'Phase 2 — Every 3 Days',
  3: 'Phase 3 — Weekly',
  4: 'Phase 4 — Bi-Weekly',
  5: 'Phase 5 — Monthly',
  6: 'Phase 6 — Every 45 Days',
  7: 'Phase 7 — Mastered',
};

const PHASE_COLORS = {
  0: [156, 163, 175],
  1: [59, 130, 246],
  2: [34, 197, 94],
  3: [234, 179, 8],
  4: [249, 115, 22],
  5: [168, 85, 247],
  6: [239, 68, 68],
  7: [16, 185, 129],
};

const getBookGroup = (reference) => {
  if (!reference) return null;
  const bookMatch = reference?.match(/^(\d\s)?[A-Za-z\s]+/);
  if (!bookMatch) return null;
  const bookName = bookMatch?.[0]?.trim();
  return BIBLE_BOOK_GROUPS?.find(g => g?.books?.some(b => bookName?.startsWith(b) || b?.startsWith(bookName))) || null;
};

const getBookNameFromRef = (reference) => {
  if (!reference) return '';
  const m = reference?.match(/^(\d\s[A-Za-z]+|[A-Za-z]+(?:\s[A-Za-z]+)?)/);
  return m ? m?.[0]?.trim() : '';
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr)?.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch { return '—'; }
};

const VersePDFExportModal = ({ isOpen, onClose, verses = [] }) => {
  const [contentType, setContentType] = useState('references_and_verses');
  const [columns, setColumns] = useState({ reviews: false, lastReviewed: false, phase: false, nextReview: false });
  const [groupBy, setGroupBy] = useState('none');
  const [isGenerating, setIsGenerating] = useState(false);

  if (!isOpen) return null;

  const toggleColumn = (key) => setColumns(prev => ({ ...prev, [key]: !prev?.[key] }));

  const generatePDF = async () => {
    setIsGenerating(true);
    try {
      const { jsPDF } = await import('jspdf');

      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageW = doc?.internal?.pageSize?.getWidth();
      const pageH = doc?.internal?.pageSize?.getHeight();
      const margin = 18;
      const contentW = pageW - margin * 2;
      let y = 0;

      // ── Color palette ──────────────────────────────────────────────────────
      const NAVY = [15, 23, 42];
      const BRAND = [99, 102, 241];   // indigo-500
      const BRAND_LIGHT = [238, 242, 255];
      const GOLD = [180, 130, 30];
      const GRAY_DARK = [55, 65, 81];
      const GRAY_MID = [107, 114, 128];
      const GRAY_LIGHT = [229, 231, 235];
      const WHITE = [255, 255, 255];
      const PAGE_BG = [250, 250, 252];

      // ── Helper: set fill ───────────────────────────────────────────────────
      const setFill = (rgb) => doc?.setFillColor(rgb?.[0], rgb?.[1], rgb?.[2]);
      const setDraw = (rgb) => doc?.setDrawColor(rgb?.[0], rgb?.[1], rgb?.[2]);
      const setTextColor = (rgb) => doc?.setTextColor(rgb?.[0], rgb?.[1], rgb?.[2]);

      // ── Helper: add new page with background ──────────────────────────────
      const addPage = () => {
        doc?.addPage();
        setFill(PAGE_BG);
        doc?.rect(0, 0, pageW, pageH, 'F');
        y = margin;
      };

      // ── Helper: check page overflow ───────────────────────────────────────
      const checkPageBreak = (neededH) => {
        if (y + neededH > pageH - margin - 12) { addPage(); return true; }
        return false;
      };

      // ── Helper: draw footer on current page ───────────────────────────────
      const drawFooter = () => {
        const pg = doc?.internal?.getCurrentPageInfo()?.pageNumber;
        const total = doc?.internal?.getNumberOfPages();
        setFill(NAVY);
        doc?.rect(0, pageH - 10, pageW, 10, 'F');
        doc?.setFontSize(7);
        doc?.setFont('helvetica', 'normal');
        setTextColor(WHITE);
        doc?.text('Fortified Mind — Scripture Memorization', margin, pageH - 3.5);
        doc?.text(`Page ${pg} of ${total}`, pageW - margin, pageH - 3.5, { align: 'right' });
        doc?.text(new Date()?.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }), pageW / 2, pageH - 3.5, { align: 'center' });
      };

      // ════════════════════════════════════════════════════════════════════════
      // COVER PAGE
      // ════════════════════════════════════════════════════════════════════════
      setFill(PAGE_BG);
      doc?.rect(0, 0, pageW, pageH, 'F');

      // Top accent bar
      setFill(NAVY);
      doc?.rect(0, 0, pageW, 38, 'F');
      setFill(BRAND);
      doc?.rect(0, 35, pageW, 3, 'F');

      // App name
      doc?.setFontSize(11);
      doc?.setFont('helvetica', 'bold');
      setTextColor(WHITE);
      doc?.text('FORTIFIED MIND', margin, 16);
      doc?.setFontSize(8);
      doc?.setFont('helvetica', 'normal');
      setTextColor([180, 190, 220]);
      doc?.text('Scripture Memorization Platform', margin, 23);

      // Cross icon area (decorative)
      setFill([30, 40, 70]);
      doc?.roundedRect(pageW - margin - 22, 8, 22, 22, 3, 3, 'F');
      setTextColor([150, 160, 200]);
      doc?.setFontSize(16);
      doc?.text('✝', pageW - margin - 11, 22, { align: 'center' });

      // Main title block
      y = 60;
      setFill(WHITE);
      doc?.roundedRect(margin, y, contentW, 52, 4, 4, 'F');
      setDraw(BRAND_LIGHT);
      doc?.setLineWidth(0.5);
      doc?.roundedRect(margin, y, contentW, 52, 4, 4, 'S');

      // Gold accent line
      setFill(GOLD);
      doc?.rect(margin, y, 4, 52, 'F');

      doc?.setFontSize(22);
      doc?.setFont('helvetica', 'bold');
      setTextColor(NAVY);
      doc?.text('Verse Library Report', margin + 12, y + 18);

      doc?.setFontSize(10);
      doc?.setFont('helvetica', 'normal');
      setTextColor(GRAY_MID);
      const contentLabel = contentType === 'references_only' ? 'References Only' : 'References & Verse Text';
      doc?.text(`Content: ${contentLabel}`, margin + 12, y + 30);

      const groupLabel = groupBy === 'phase' ? 'Grouped by Phase' : groupBy === 'book_group' ? 'Grouped by Bible Book Group' : 'No Grouping';
      doc?.text(`Organization: ${groupLabel}`, margin + 12, y + 39);
      doc?.text(`Generated: ${new Date()?.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`, margin + 12, y + 48);

      // Stats row
      y += 68;
      const statBoxW = (contentW - 8) / 3;
      const statItems = [
        { label: 'Total Verses', value: verses?.length },
        { label: 'Phases', value: [...new Set(verses.map(v => v?.currentPhase ?? 0))]?.length },
        { label: 'Books', value: [...new Set(verses.map(v => getBookNameFromRef(v?.reference)).filter(Boolean))]?.length },
      ];
      statItems?.forEach((item, i) => {
        const sx = margin + i * (statBoxW + 4);
        setFill(BRAND_LIGHT);
        doc?.roundedRect(sx, y, statBoxW, 22, 3, 3, 'F');
        setDraw([200, 210, 255]);
        doc?.setLineWidth(0.3);
        doc?.roundedRect(sx, y, statBoxW, 22, 3, 3, 'S');
        doc?.setFontSize(16);
        doc?.setFont('helvetica', 'bold');
        setTextColor(BRAND);
        doc?.text(String(item?.value), sx + statBoxW / 2, y + 12, { align: 'center' });
        doc?.setFontSize(7.5);
        doc?.setFont('helvetica', 'normal');
        setTextColor(GRAY_MID);
        doc?.text(item?.label, sx + statBoxW / 2, y + 19, { align: 'center' });
      });

      // Optional columns note
      y += 32;
      const activeColumns = Object.entries(columns)?.filter(([, v]) => v)?.map(([k]) => (({
        reviews: 'Number of Reviews',
        lastReviewed: 'Date Last Reviewed',
        phase: 'Phase',
        nextReview: 'Next Review Date'
      })?.[k]));
      if (activeColumns?.length > 0) {
        setFill([245, 247, 255]);
        doc?.roundedRect(margin, y, contentW, 14, 3, 3, 'F');
        doc?.setFontSize(8);
        doc?.setFont('helvetica', 'italic');
        setTextColor(GRAY_MID);
        doc?.text(`Includes: ${activeColumns?.join(' · ')}`, margin + 6, y + 9);
        y += 20;
      }

      // Scripture quote
      y = pageH - 60;
      setFill([240, 244, 255]);
      doc?.roundedRect(margin, y, contentW, 28, 4, 4, 'F');
      setFill(BRAND);
      doc?.rect(margin, y, 3, 28, 'F');
      doc?.setFontSize(9);
      doc?.setFont('helvetica', 'italic');
      setTextColor(NAVY);
      doc?.text('"Your word I have hidden in my heart, that I might not sin against You."', margin + 8, y + 10, { maxWidth: contentW - 12 });
      doc?.setFontSize(8);
      doc?.setFont('helvetica', 'normal');
      setTextColor(GRAY_MID);
      doc?.text('— Psalm 119:11', margin + 8, y + 22);

      drawFooter();

      // ════════════════════════════════════════════════════════════════════════
      // CONTENT PAGES
      // ════════════════════════════════════════════════════════════════════════
      addPage();

      // ── Build column definitions ───────────────────────────────────────────
      const showVerse = contentType === 'references_and_verses';
      const colDefs = [];
      // Reference always first
      colDefs?.push({ key: 'reference', label: 'Reference', width: showVerse ? 38 : 55 });
      if (showVerse) colDefs?.push({ key: 'verse', label: 'Verse Text', width: null }); // flex
      if (columns?.phase) colDefs?.push({ key: 'phase', label: 'Phase', width: 22 });
      if (columns?.reviews) colDefs?.push({ key: 'reviews', label: 'Reviews', width: 18 });
      if (columns?.lastReviewed) colDefs?.push({ key: 'lastReviewed', label: 'Last Reviewed', width: 28 });
      if (columns?.nextReview) colDefs?.push({ key: 'nextReview', label: 'Next Review', width: 26 });

      // Distribute remaining width to verse text column
      const fixedW = colDefs?.reduce((s, c) => s + (c?.width || 0), 0);
      const flexCol = colDefs?.find(c => c?.width === null);
      if (flexCol) flexCol.width = contentW - fixedW;

      // ── Helper: draw table header row ─────────────────────────────────────
      const drawTableHeader = () => {
        setFill(NAVY);
        doc?.rect(margin, y, contentW, 8, 'F');
        doc?.setFontSize(7.5);
        doc?.setFont('helvetica', 'bold');
        setTextColor(WHITE);
        let cx = margin + 3;
        colDefs?.forEach(col => {
          doc?.text(col?.label, cx, y + 5.5);
          cx += col?.width;
        });
        y += 8;
      };

      // ── Helper: draw a single verse row ───────────────────────────────────
      const drawVerseRow = (verse, rowIndex) => {
        const isEven = rowIndex % 2 === 0;
        const phase = verse?.currentPhase ?? 0;
        const lastReviewed = verse?.lastReviewedAt || verse?.last_reviewed_at;
        const nextReview = verse?.nextReviewDate || verse?.next_review_date;

        // Estimate row height
        let rowH = 7;
        if (showVerse && verse?.text) {
          const verseCol = colDefs?.find(c => c?.key === 'verse');
          const lines = doc?.splitTextToSize(verse?.text || '', (verseCol?.width || 80) - 4);
          rowH = Math.max(7, lines?.length * 4 + 3);
        }

        checkPageBreak(rowH);

        // Row background
        if (isEven) {
          setFill([245, 247, 255]);
        } else {
          setFill(WHITE);
        }
        doc?.rect(margin, y, contentW, rowH, 'F');

        // Left phase color accent
        const phaseRgb = PHASE_COLORS?.[phase] || PHASE_COLORS?.[0];
        setFill(phaseRgb);
        doc?.rect(margin, y, 2, rowH, 'F');

        doc?.setFontSize(7.5);
        let cx = margin + 3;

        colDefs?.forEach(col => {
          doc?.setFont('helvetica', col?.key === 'reference' ? 'bold' : 'normal');
          setTextColor(col?.key === 'reference' ? NAVY : GRAY_DARK);

          if (col?.key === 'reference') {
            doc?.text(verse?.reference || '', cx, y + rowH / 2 + 1.5, { baseline: 'middle' });
          } else if (col?.key === 'verse') {
            const lines = doc?.splitTextToSize(verse?.text || '', col?.width - 4);
            doc?.setFont('helvetica', 'normal');
            setTextColor(GRAY_DARK);
            lines?.forEach((line, li) => {
              doc?.text(line, cx, y + 3.5 + li * 4);
            });
          } else if (col?.key === 'phase') {
            const phaseLabel = phase === 0 ? 'Not Started' : `Phase ${phase}`;
            // Phase badge
            setFill(phaseRgb);
            doc?.roundedRect(cx, y + rowH / 2 - 2.5, col?.width - 3, 5, 1, 1, 'F');
            doc?.setFontSize(6.5);
            doc?.setFont('helvetica', 'bold');
            setTextColor(WHITE);
            doc?.text(phaseLabel, cx + (col?.width - 3) / 2, y + rowH / 2 + 1, { align: 'center' });
            doc?.setFontSize(7.5);
          } else if (col?.key === 'reviews') {
            setTextColor(GRAY_DARK);
            doc?.text(String(verse?.reviewCount || 0), cx + col?.width / 2 - 3, y + rowH / 2 + 1.5, { align: 'center' });
          } else if (col?.key === 'lastReviewed') {
            setTextColor(GRAY_MID);
            doc?.text(formatDate(lastReviewed), cx, y + rowH / 2 + 1.5);
          } else if (col?.key === 'nextReview') {
            setTextColor(GRAY_MID);
            doc?.text(formatDate(nextReview), cx, y + rowH / 2 + 1.5);
          }
          cx += col?.width;
        });

        // Bottom border
        setDraw(GRAY_LIGHT);
        doc?.setLineWidth(0.1);
        doc?.line(margin, y + rowH, margin + contentW, y + rowH);

        y += rowH;
      };

      // ── Helper: draw section header ────────────────────────────────────────
      const drawSectionHeader = (title, subtitle, colorRgb) => {
        checkPageBreak(18);
        setFill(colorRgb || BRAND);
        doc?.roundedRect(margin, y, contentW, 12, 2, 2, 'F');
        doc?.setFontSize(9);
        doc?.setFont('helvetica', 'bold');
        setTextColor(WHITE);
        doc?.text(title, margin + 6, y + 8);
        if (subtitle) {
          doc?.setFontSize(7.5);
          doc?.setFont('helvetica', 'normal');
          setTextColor([200, 210, 240]);
          doc?.text(subtitle, pageW - margin - 4, y + 8, { align: 'right' });
        }
        y += 14;
      };

      // ── Group and render verses ────────────────────────────────────────────
      if (groupBy === 'phase') {
        const phases = [...new Set(verses.map(v => v?.currentPhase ?? 0))]?.sort((a, b) => a - b);
        phases?.forEach(phase => {
          const group = verses?.filter(v => (v?.currentPhase ?? 0) === phase);
          const phaseRgb = PHASE_COLORS?.[phase] || PHASE_COLORS?.[0];
          drawSectionHeader(PHASE_LABELS?.[phase] || `Phase ${phase}`, `${group?.length} verse${group?.length !== 1 ? 's' : ''}`, phaseRgb);
          drawTableHeader();
          group?.forEach((verse, i) => drawVerseRow(verse, i));
          y += 6;
        });
      } else if (groupBy === 'book_group') {
        BIBLE_BOOK_GROUPS?.forEach(group => {
          const groupVerses = verses?.filter(v => {
            const bookName = getBookNameFromRef(v?.reference);
            return group?.books?.some(b => bookName === b || bookName?.startsWith(b));
          });
          if (groupVerses?.length === 0) return;
          const testament = group?.testament === 'OT' ? 'Old Testament' : 'New Testament';
          const headerColor = group?.testament === 'OT' ? [120, 80, 20] : [30, 64, 175];
          drawSectionHeader(`${group?.label}`, `${testament} · ${groupVerses?.length} verse${groupVerses?.length !== 1 ? 's' : ''}`, headerColor);
          drawTableHeader();
          groupVerses?.forEach((verse, i) => drawVerseRow(verse, i));
          y += 6;
        });
      } else {
        // No grouping — single table
        drawTableHeader();
        verses?.forEach((verse, i) => drawVerseRow(verse, i));
      }

      // ── Add footers to all pages ───────────────────────────────────────────
      const totalPages = doc?.internal?.getNumberOfPages();
      for (let p = 1; p <= totalPages; p++) {
        doc?.setPage(p);
        drawFooter();
      }

      // ── Save ───────────────────────────────────────────────────────────────
      const dateStr = new Date()?.toISOString()?.split('T')?.[0];
      doc?.save(`verse-library-report-${dateStr}.pdf`);
    } catch (err) {
      console.error('PDF generation failed:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      {/* Modal */}
      <div className="relative bg-card border border-border rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Icon name="FileText" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-heading font-bold text-foreground">Export Verse Report</h2>
              <p className="text-xs text-muted-foreground">{verses?.length} verse{verses?.length !== 1 ? 's' : ''} in your library</p>
            </div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-lg hover:bg-muted">
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Content Type */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="BookOpen" size={15} className="text-primary" />
              Content to Include
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {[
                { value: 'references_and_verses', label: 'References + Verses', desc: 'Reference and full verse text', icon: 'AlignLeft' },
                { value: 'references_only', label: 'References Only', desc: 'Scripture references only', icon: 'List' },
              ]?.map(opt => (
                <button
                  key={opt?.value}
                  onClick={() => setContentType(opt?.value)}
                  className={`p-3 rounded-xl border-2 text-left transition-all ${
                    contentType === opt?.value
                      ? 'border-primary bg-primary/5' :'border-border bg-background hover:border-primary/40'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Icon name={opt?.icon} size={14} className={contentType === opt?.value ? 'text-primary' : 'text-muted-foreground'} />
                    <span className={`text-xs font-semibold ${contentType === opt?.value ? 'text-primary' : 'text-foreground'}`}>{opt?.label}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{opt?.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Optional Columns */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="Columns" size={15} className="text-primary" />
              Optional Columns
            </h3>
            <div className="grid grid-cols-2 gap-2">
              {[
                { key: 'phase', label: 'Phase', icon: 'Layers' },
                { key: 'reviews', label: 'Number of Reviews', icon: 'RotateCcw' },
                { key: 'lastReviewed', label: 'Date Last Reviewed', icon: 'Clock' },
                { key: 'nextReview', label: 'Next Review Date', icon: 'CalendarClock' },
              ]?.map(col => (
                <button
                  key={col?.key}
                  onClick={() => toggleColumn(col?.key)}
                  className={`flex items-center gap-2 p-2.5 rounded-lg border transition-all text-left ${
                    columns?.[col?.key]
                      ? 'border-primary/50 bg-primary/5 text-primary' :'border-border bg-background text-muted-foreground hover:border-primary/30'
                  }`}
                >
                  <div className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border ${
                    columns?.[col?.key] ? 'bg-primary border-primary' : 'border-border'
                  }`}>
                    {columns?.[col?.key] && <Icon name="Check" size={10} className="text-white" />}
                  </div>
                  <Icon name={col?.icon} size={13} />
                  <span className="text-xs font-medium">{col?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Group By */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="FolderOpen" size={15} className="text-primary" />
              Group By
            </h3>
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: 'none', label: 'No Grouping', icon: 'Minus' },
                { value: 'phase', label: 'Phase', icon: 'Layers' },
                { value: 'book_group', label: 'Book Group', icon: 'BookMarked' },
              ]?.map(opt => (
                <button
                  key={opt?.value}
                  onClick={() => setGroupBy(opt?.value)}
                  className={`p-2.5 rounded-lg border-2 text-center transition-all ${
                    groupBy === opt?.value
                      ? 'border-primary bg-primary/5' :'border-border bg-background hover:border-primary/40'
                  }`}
                >
                  <Icon name={opt?.icon} size={16} className={`mx-auto mb-1 ${groupBy === opt?.value ? 'text-primary' : 'text-muted-foreground'}`} />
                  <span className={`text-xs font-medium block ${groupBy === opt?.value ? 'text-primary' : 'text-foreground'}`}>{opt?.label}</span>
                </button>
              ))}
            </div>
            {groupBy === 'book_group' && (
              <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                <Icon name="Info" size={11} />
                Groups by canonical Bible sections (Pentateuch, Gospels, etc.)
              </p>
            )}
          </div>

          {/* Preview summary */}
          <div className="bg-muted/40 rounded-xl p-4 border border-border">
            <h4 className="text-xs font-semibold text-foreground mb-2 flex items-center gap-1.5">
              <Icon name="Eye" size={12} className="text-muted-foreground" />
              Report Preview
            </h4>
            <div className="space-y-1 text-xs text-muted-foreground">
              <div className="flex items-center gap-2">
                <Icon name="Check" size={11} className="text-success flex-shrink-0" />
                <span>Cover page with statistics summary</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="Check" size={11} className="text-success flex-shrink-0" />
                <span>{contentType === 'references_only' ? 'References only' : 'References with full verse text'}</span>
              </div>
              {Object.entries(columns)?.filter(([, v]) => v)?.map(([k]) => (
                <div key={k} className="flex items-center gap-2">
                  <Icon name="Check" size={11} className="text-success flex-shrink-0" />
                  <span>{{ reviews: 'Review count column', lastReviewed: 'Last reviewed date column', phase: 'Phase column', nextReview: 'Next review date column' }?.[k]}</span>
                </div>
              ))}
              <div className="flex items-center gap-2">
                <Icon name="Check" size={11} className="text-success flex-shrink-0" />
                <span>{groupBy === 'phase' ? 'Grouped by memorization phase' : groupBy === 'book_group' ? 'Grouped by Bible book section' : 'All verses in one table'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border bg-muted/20">
          <Button variant="outline" onClick={onClose} className="rounded-full">
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={generatePDF}
            disabled={isGenerating}
            iconName={isGenerating ? 'Loader2' : 'Download'}
            iconPosition="left"
            className="rounded-full bg-primary hover:bg-primary/90 text-white min-w-[160px]"
          >
            {isGenerating ? 'Generating PDF...' : 'Export PDF'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default VersePDFExportModal;
